import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  Badge,
  Container
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  AccessTime as AccessTimeIcon,
  PlayArrow as PlayArrowIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import api from '../../services/api';
import { formatDistance, format, parseISO } from 'date-fns';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { TimeField } from '@mui/x-date-pickers/TimeField';

// Schema for form validation
const scheduleSchema = yup.object().shape({
  frequency: yup.string().required('Frequency is required'),
  reportType: yup.string().required('Report type is required'),
  timeOfDay: yup.string().required('Time of day is required'),
  recipients: yup.array().of(
    yup.string().email('Must be a valid email')
  ).required('At least one recipient is required'),
  includeCharts: yup.boolean(),
  includeTopCategories: yup.boolean(),
  isActive: yup.boolean(),
  tenantName: yup.string(),
  showCategoryNames: yup.boolean(),
  // Conditional validation
  dayOfWeek: yup.number().when('frequency', {
    is: 'weekly',
    then: yup.number().min(0).max(6).required('Day of week is required for weekly reports')
  }),
  dayOfMonth: yup.number().when('frequency', {
    is: 'monthly',
    then: yup.number().min(1).max(31).required('Day of month is required for monthly reports')
  })
});

const FinancialReportScheduler = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [recipientInput, setRecipientInput] = useState('');
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailData, setEmailData] = useState({
    frequency: 'daily',
    reportType: 'summary',
    recipients: [],
    includeCharts: true,
    includeTopCategories: true,
    tenantName: localStorage.getItem('tenantName') || '', // Ensure tenant name is correctly set
    showCategoryNames: true // Add option to show real category names instead of uncategorized
  });
  
  // Initialize form with React Hook Form
  const { register, handleSubmit, setValue, reset, watch, control, formState: { errors } } = useForm({
    resolver: yupResolver(scheduleSchema),
    defaultValues: {
      frequency: 'daily',
      reportType: 'summary',
      timeOfDay: '08:00',
      recipients: [],
      dayOfWeek: 1, // Monday
      dayOfMonth: 1,
      includeCharts: true,
      includeTopCategories: true,
      isActive: true,
      tenantName: localStorage.getItem('tenantName') || '', // Ensure tenant name is correctly set
      showCategoryNames: true // Add option to show real category names
    }
  });
  
  // Watch for changes in frequency
  const watchFrequency = watch('frequency');
  const watchRecipients = watch('recipients');
  
  // Load schedules on component mount
  useEffect(() => {
    loadSchedules();
  }, []);
  
  // Load all existing report schedules
  const loadSchedules = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/financial-reports/schedules');
      
      if (response.data && response.data.success) {
        setSchedules(response.data.data);
      } else {
        setError('Failed to load schedules: ' + (response.data?.message || 'Unknown error'));
      }
    } catch (err) {
      setError('Error loading schedules: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  // Open dialog to create new schedule
  const openNewScheduleDialog = () => {
    const tenantName = localStorage.getItem('tenantName') || '';
    reset({
      frequency: 'daily',
      reportType: 'summary',
      timeOfDay: '08:00',
      recipients: [],
      dayOfWeek: 1,
      dayOfMonth: 1,
      includeCharts: true,
      includeTopCategories: true,
      isActive: true,
      tenantName: tenantName, // Add tenant name
      showCategoryNames: true // Add show category names option
    });
    setEditMode(false);
    setIsDialogOpen(true);
  };
  
  // Open dialog to edit existing schedule
  const openEditScheduleDialog = (schedule) => {
    // If schedule doesn't have a tenant name, get it from localStorage
    const tenantName = schedule.tenantName || localStorage.getItem('tenantName') || '';
    reset({
      frequency: schedule.frequency,
      reportType: schedule.reportType,
      timeOfDay: schedule.timeOfDay,
      recipients: schedule.recipients || [],
      dayOfWeek: schedule.dayOfWeek,
      dayOfMonth: schedule.dayOfMonth,
      includeCharts: schedule.includeCharts,
      includeTopCategories: schedule.includeTopCategories,
      isActive: schedule.isActive,
      tenantName: tenantName, // Add tenant name
      showCategoryNames: schedule.showCategoryNames || true // Add show category names option
    });
    setSelectedSchedule(schedule);
    setEditMode(true);
    setIsDialogOpen(true);
  };
  
  // Open dialog to confirm schedule deletion
  const openDeleteDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setIsDeleteDialogOpen(true);
  };
  
  // Open dialog to send email report manually
  const openEmailDialog = () => {
    const tenantName = localStorage.getItem('tenantName') || '';
    setEmailData({
      frequency: 'daily',
      reportType: 'summary',
      recipients: [],
      includeCharts: true,
      includeTopCategories: true,
      tenantName: tenantName, // Ensure tenant name is properly set
      showCategoryNames: true // Add show category names option
    });
    setIsEmailDialogOpen(true);
  };
  
  // Handle adding a recipient email
  const handleAddRecipient = () => {
    if (recipientInput && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientInput)) {
      const updatedRecipients = [...watchRecipients, recipientInput];
      setValue('recipients', updatedRecipients);
      setRecipientInput('');
    }
  };
  
  // Handle removing a recipient
  const handleRemoveRecipient = (index) => {
    const updatedRecipients = watchRecipients.filter((_, i) => i !== index);
    setValue('recipients', updatedRecipients);
  };
  
  // Handle adding a recipient email to manual email form
  const handleAddEmailRecipient = () => {
    if (recipientInput && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientInput)) {
      setEmailData({
        ...emailData,
        recipients: [...emailData.recipients, recipientInput]
      });
      setRecipientInput('');
    }
  };
  
  // Handle removing a recipient from manual email form
  const handleRemoveEmailRecipient = (index) => {
    const updatedRecipients = emailData.recipients.filter((_, i) => i !== index);
    setEmailData({
      ...emailData,
      recipients: updatedRecipients
    });
  };
  
  // Handle form submission for new or edited schedule
  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      // Get the tenant ID and name from localStorage
      const tenantId = localStorage.getItem('tenantId');
      const tenantName = localStorage.getItem('tenantName');
      
      // Format time for API and include tenant ID and name
      const formattedData = {
        ...data,
        tenant: tenantId,
        tenantName: tenantName, // Ensure tenant name is explicitly included
        showCategoryNames: true // Add option to show real category names
      };
      
      let response;
      
      if (editMode) {
        // Update existing schedule
        response = await api.put(`/financial-reports/schedules/${selectedSchedule._id}`, formattedData);
        setSuccess('Schedule updated successfully');
      } else {
        // Create new schedule
        response = await api.post('/financial-reports/schedules', formattedData);
        setSuccess('Schedule created successfully');
      }
      
      if (response.data && response.data.success) {
        setIsDialogOpen(false);
        // Reload schedules to show the updated list
        loadSchedules();
      } else {
        setError('Failed to save schedule: ' + (response.data?.message || 'Unknown error'));
      }
    } catch (err) {
      setError('Error saving schedule: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle schedule deletion
  const handleDeleteSchedule = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const response = await api.delete(`/financial-reports/schedules/${selectedSchedule._id}`);
      
      if (response.data && response.data.success) {
        setSuccess('Schedule deleted successfully');
        setIsDeleteDialogOpen(false);
        loadSchedules();
      } else {
        setError('Failed to delete schedule: ' + (response.data?.message || 'Unknown error'));
      }
    } catch (err) {
      setError('Error deleting schedule: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle sending a manual email report
  const handleSendEmailReport = async () => {
    try {
      setEmailLoading(true);
      setError(null);
      setSuccess(null);
      
      if (!emailData.recipients || emailData.recipients.length === 0) {
        setError('At least one recipient is required');
        setEmailLoading(false);
        return;
      }
      
      // Get tenant name from localStorage if not already in emailData
      const tenantName = localStorage.getItem('tenantName');
      const emailDataWithTenant = {
        ...emailData,
        tenantName: tenantName, // Ensure tenant name is included
        showCategoryNames: true // Ensure category names are shown
      };
      
      const response = await api.post('/financial-reports/generate-and-email', emailDataWithTenant);
      
      if (response.data && response.data.success) {
        setSuccess('Report generated and sent successfully');
        setIsEmailDialogOpen(false);
      } else {
        setError('Failed to send report: ' + (response.data?.message || 'Unknown error'));
      }
    } catch (err) {
      setError('Error sending report: ' + (err.response?.data?.message || err.message));
    } finally {
      setEmailLoading(false);
    }
  };
  
  // Format next run time for display
  const formatNextRun = (dateString) => {
    if (!dateString) return 'Not scheduled';
    
    try {
      const date = parseISO(dateString);
      const fromNow = formatDistance(date, new Date(), { addSuffix: true });
      const formatted = format(date, 'PPpp'); // 'Mon, Jan 1, 2023, 12:00 PM'
      
      return (
        <Tooltip title={formatted}>
          <span>{fromNow}</span>
        </Tooltip>
      );
    } catch (err) {
      return 'Invalid date';
    }
  };
  
  // Get day of week string
  const getDayOfWeekString = (day) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[day] || 'Unknown';
  };
  
  // Change "detailed" to "Detailed" in the UI
  const formatReportType = (type) => {
    if (type === 'summary') return 'Summary';
    if (type === 'detailed') return 'Detailed';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Render status chip for a schedule
  const renderStatusChip = (schedule) => {
    if (!schedule.lastRunStatus) {
      return (
        <Chip 
          label="Pending" 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      );
    }
    
    switch (schedule.lastRunStatus) {
      case 'success':
        return (
          <Tooltip title={`Last run: ${schedule.lastRunTime ? format(parseISO(schedule.lastRunTime), 'PPpp') : 'Unknown'}`}>
            <Chip 
              icon={<CheckIcon />} 
              label="Success" 
              size="small" 
              color="success" 
              variant="outlined" 
            />
          </Tooltip>
        );
      case 'error':
        return (
          <Tooltip title={schedule.lastRunMessage || 'Error occurred'}>
            <Chip 
              icon={<ErrorIcon />} 
              label="Failed" 
              size="small" 
              color="error" 
              variant="outlined" 
            />
          </Tooltip>
        );
      default:
        return (
          <Chip 
            label="Unknown" 
            size="small" 
            color="default" 
            variant="outlined"
          />
        );
    }
  };
  
  // Render schedule details
  const renderScheduleDetails = (schedule) => {
    let details = '';
    
    switch (schedule.frequency) {
      case 'daily':
        details = `Daily at ${schedule.timeOfDay}`;
        break;
      case 'weekly':
        details = `Every ${getDayOfWeekString(schedule.dayOfWeek)} at ${schedule.timeOfDay}`;
        break;
      case 'monthly':
        details = `Monthly on day ${schedule.dayOfMonth} at ${schedule.timeOfDay}`;
        break;
      default:
        details = 'Custom schedule';
    }
    
    return details;
  };

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: { xs: 1, md: 4 }, 
        mb: 4,
        px: { xs: 2, md: 3 }
      }}
    >
      <Box sx={{ py: 4, px: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4" component="h1" gutterBottom>
            Financial Report Scheduler
          </Typography>
          <Box>
            <Button 
              variant="outlined" 
              startIcon={<EmailIcon />} 
              onClick={openEmailDialog} 
              sx={{ mr: 2 }}
            >
              Send One-Time Report
            </Button>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={openNewScheduleDialog}
            >
              New Schedule
            </Button>
          </Box>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}
        
        <Paper 
          elevation={3} 
          sx={{ p: 2, mb: 4 }}
        >
          <Typography variant="h6" gutterBottom>
            About Financial Report Scheduling
          </Typography>
          <Typography variant="body1" paragraph>
            This tool allows you to schedule automated financial reports to be sent via email on a regular basis.
            You can configure daily, weekly, or monthly schedules and customize the content included in each report.
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Daily Reports
                </Typography>
                <Typography variant="body2">
                  Receive daily summaries of financial activity at your specified time each day.
                  Ideal for keeping a close eye on day-to-day transactions.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ bgcolor: 'secondary.light', color: 'secondary.contrastText', p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Weekly Reports
                </Typography>
                <Typography variant="body2">
                  Get comprehensive weekly financial reports on your chosen day of the week.
                  Perfect for weekly financial reviews and planning.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ bgcolor: 'info.light', color: 'info.contrastText', p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Monthly Reports
                </Typography>
                <Typography variant="body2">
                  Receive detailed monthly financial reports on your specified day of each month.
                  Ideal for month-end reconciliation and financial analysis.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        <Typography variant="h5" sx={{ mb: 2 }}>
          Scheduled Reports
        </Typography>
        
        {loading && !isDialogOpen && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        )}
        
        {!loading && schedules.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No report schedules have been set up yet. Click "New Schedule" to create one.
          </Alert>
        )}
        
        <Grid container spacing={3}>
          {schedules.map(schedule => (
            <Grid item xs={12} md={6} lg={4} key={schedule._id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderLeft: 6,
                  borderColor: schedule.isActive ? 'primary.main' : 'grey.400',
                  opacity: schedule.isActive ? 1 : 0.7
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" gutterBottom>
                      {schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1)} Report
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={schedule.isActive} 
                          onChange={async () => {
                            try {
                              await api.put(`/financial-reports/schedules/${schedule._id}`, {
                                isActive: !schedule.isActive
                              });
                              loadSchedules();
                            } catch (err) {
                              setError('Error updating schedule status: ' + err.message);
                            }
                          }}
                          size="small"
                        />
                      }
                      label={schedule.isActive ? "Active" : "Inactive"}
                    />
                  </Box>
                  
                  <Box display="flex" alignItems="center" mb={1}>
                    <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {renderScheduleDetails(schedule)}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" mb={1}>
                    <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      Next run: {formatNextRun(schedule.nextRunTime)}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" mb={1}>
                    <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {schedule.recipients && schedule.recipients.length > 0 
                        ? `${schedule.recipients.length} recipient${schedule.recipients.length !== 1 ? 's' : ''}`
                        : 'No recipients'}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      Status:
                    </Typography>
                    {renderStatusChip(schedule)}
                  </Box>
                  
                  <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Report Type:</strong> {formatReportType(schedule.reportType)}
                    </Typography>
                    
                    <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                      {schedule.includeCharts && (
                        <Chip 
                          label="Charts" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      )}
                      {schedule.includeTopCategories && (
                        <Chip 
                          label="Top Categories" 
                          size="small" 
                          color="secondary" 
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </CardContent>
                
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Box display="flex" justifyContent="space-between" width="100%">
                    <Button 
                      startIcon={<PlayArrowIcon />} 
                      size="small" 
                      onClick={async () => {
                        try {
                          setLoading(true);
                          
                          const response = await api.post('/financial-reports/generate-and-email', {
                            frequency: schedule.frequency,
                            reportType: schedule.reportType,
                            recipients: schedule.recipients,
                            includeCharts: schedule.includeCharts,
                            includeTopCategories: schedule.includeTopCategories,
                            tenantName: schedule.tenantName || localStorage.getItem('tenantName') || '', // Add tenant name
                            showCategoryNames: schedule.showCategoryNames || true // Add show category names option
                          });
                          
                          if (response.data && response.data.success) {
                            setSuccess('Report generated and sent successfully');
                          } else {
                            setError('Failed to send report: ' + (response.data?.message || 'Unknown error'));
                          }
                        } catch (err) {
                          setError('Error sending report: ' + (err.response?.data?.message || err.message));
                        } finally {
                          setLoading(false);
                        }
                      }}
                    >
                      Run Now
                    </Button>
                    
                    <Box>
                      <IconButton 
                        color="primary" 
                        size="small"
                        onClick={() => openEditScheduleDialog(schedule)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        size="small"
                        onClick={() => openDeleteDialog(schedule)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {/* Schedule Dialog Form */}
        <Dialog 
          open={isDialogOpen} 
          onClose={() => setIsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>
              {editMode ? 'Edit Report Schedule' : 'Create New Report Schedule'}
            </DialogTitle>
            
            <DialogContent>
              <Grid container spacing={3} sx={{ mt: 0 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.frequency} sx={{ mb: 2 }}>
                    <InputLabel>Frequency</InputLabel>
                    <Controller
                      name="frequency"
                      control={control}
                      render={({ field }) => (
                        <Select 
                          {...field}
                          label="Frequency"
                        >
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                        </Select>
                      )}
                    />
                    {errors.frequency && (
                      <FormHelperText>{errors.frequency.message}</FormHelperText>
                    )}
                  </FormControl>
                  
                  {watchFrequency === 'weekly' && (
                    <FormControl fullWidth error={!!errors.dayOfWeek} sx={{ mb: 2 }}>
                      <InputLabel>Day of Week</InputLabel>
                      <Controller
                        name="dayOfWeek"
                        control={control}
                        render={({ field }) => (
                          <Select 
                            {...field}
                            label="Day of Week"
                          >
                            <MenuItem value={0}>Sunday</MenuItem>
                            <MenuItem value={1}>Monday</MenuItem>
                            <MenuItem value={2}>Tuesday</MenuItem>
                            <MenuItem value={3}>Wednesday</MenuItem>
                            <MenuItem value={4}>Thursday</MenuItem>
                            <MenuItem value={5}>Friday</MenuItem>
                            <MenuItem value={6}>Saturday</MenuItem>
                          </Select>
                        )}
                      />
                      {errors.dayOfWeek && (
                        <FormHelperText>{errors.dayOfWeek.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                  
                  {watchFrequency === 'monthly' && (
                    <FormControl fullWidth error={!!errors.dayOfMonth} sx={{ mb: 2 }}>
                      <InputLabel>Day of Month</InputLabel>
                      <Controller
                        name="dayOfMonth"
                        control={control}
                        render={({ field }) => (
                          <Select 
                            {...field}
                            label="Day of Month"
                          >
                            {[...Array(31)].map((_, i) => (
                              <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.dayOfMonth && (
                        <FormHelperText>{errors.dayOfMonth.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                  
                  <FormControl fullWidth error={!!errors.timeOfDay} sx={{ mb: 2 }}>
                    <Controller
                      name="timeOfDay"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Time of Day"
                          type="time"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          error={!!errors.timeOfDay}
                          helperText={errors.timeOfDay?.message}
                        />
                      )}
                    />
                  </FormControl>
                  
                  <FormControl fullWidth error={!!errors.reportType} sx={{ mb: 2 }}>
                    <InputLabel>Report Type</InputLabel>
                    <Controller
                      name="reportType"
                      control={control}
                      render={({ field }) => (
                        <Select 
                          {...field}
                          label="Report Type"
                        >
                          <MenuItem value="summary">Summary (Key Metrics Only)</MenuItem>
                          <MenuItem value="detailed">Detailed (With Transactions)</MenuItem>
                        </Select>
                      )}
                    />
                    {errors.reportType && (
                      <FormHelperText>{errors.reportType.message}</FormHelperText>
                    )}
                  </FormControl>
                  
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Controller
                          name="includeCharts"
                          control={control}
                          render={({ field }) => (
                            <Switch 
                              checked={field.value} 
                              onChange={(e) => field.onChange(e.target.checked)} 
                            />
                          )}
                        />
                      }
                      label="Include Charts"
                    />
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Controller
                          name="includeTopCategories"
                          control={control}
                          render={({ field }) => (
                            <Switch 
                              checked={field.value} 
                              onChange={(e) => field.onChange(e.target.checked)} 
                            />
                          )}
                        />
                      }
                      label="Include Top Categories"
                    />
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Controller
                          name="isActive"
                          control={control}
                          render={({ field }) => (
                            <Switch 
                              checked={field.value} 
                              onChange={(e) => field.onChange(e.target.checked)} 
                            />
                          )}
                        />
                      }
                      label="Active"
                    />
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Controller
                          name="showCategoryNames"
                          control={control}
                          render={({ field }) => (
                            <Switch 
                              checked={field.value} 
                              onChange={(e) => field.onChange(e.target.checked)} 
                            />
                          )}
                        />
                      }
                      label="Show Proper Category Names"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Recipients
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Add Recipient Email"
                        value={recipientInput}
                        onChange={(e) => setRecipientInput(e.target.value)}
                        error={!!errors.recipients}
                        helperText={errors.recipients?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button 
                                onClick={handleAddRecipient}
                                disabled={!recipientInput || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientInput)}
                              >
                                Add
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddRecipient();
                          }
                        }}
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {watchRecipients && watchRecipients.map((email, index) => (
                        <Chip
                          key={index}
                          label={email}
                          onDelete={() => handleRemoveRecipient(index)}
                          size="medium"
                        />
                      ))}
                    </Box>
                    
                    {watchRecipients?.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        No recipients added yet
                      </Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Schedule Summary
                    </Typography>
                    
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="body2" paragraph>
                        <strong>Frequency:</strong> {
                          watchFrequency === 'daily' ? 'Daily' :
                          watchFrequency === 'weekly' ? `Weekly on ${getDayOfWeekString(watch('dayOfWeek'))}` :
                          watchFrequency === 'monthly' ? `Monthly on day ${watch('dayOfMonth')}` : 'Custom'
                        }
                      </Typography>
                      
                      <Typography variant="body2" paragraph>
                        <strong>Time:</strong> {watch('timeOfDay')}
                      </Typography>
                      
                      <Typography variant="body2" paragraph>
                        <strong>Recipients:</strong> {watchRecipients?.length || 0}
                      </Typography>
                      
                      <Typography variant="body2">
                        <strong>Report Type:</strong> {formatReportType(watch('reportType'))}
                        {watch('includeCharts') && ' with charts'}
                        {watch('includeTopCategories') && ' and top categories'}
                      </Typography>
                    </Paper>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button 
                onClick={() => setIsDialogOpen(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    Saving...
                  </>
                ) : (
                  editMode ? 'Update Schedule' : 'Create Schedule'
                )}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this {selectedSchedule?.frequency} report schedule?
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteSchedule}
              color="error"
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Delete Schedule'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Manual Email Dialog */}
        <Dialog
          open={isEmailDialogOpen}
          onClose={() => setIsEmailDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Send One-Time Financial Report</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={emailData.reportType}
                  onChange={(e) => setEmailData({ ...emailData, reportType: e.target.value })}
                  label="Report Type"
                >
                  <MenuItem value="summary">Summary (Key Metrics Only)</MenuItem>
                  <MenuItem value="detailed">Detailed (With Transactions)</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={emailData.frequency}
                  onChange={(e) => setEmailData({ ...emailData, frequency: e.target.value })}
                  label="Time Period"
                >
                  <MenuItem value="daily">Daily (Last 24 Hours)</MenuItem>
                  <MenuItem value="weekly">Weekly (Last 7 Days)</MenuItem>
                  <MenuItem value="monthly">Monthly (Last 30 Days)</MenuItem>
                </Select>
              </FormControl>
              
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailData.includeCharts}
                      onChange={(e) => setEmailData({ ...emailData, includeCharts: e.target.checked })}
                    />
                  }
                  label="Include Charts"
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailData.includeTopCategories}
                      onChange={(e) => setEmailData({ ...emailData, includeTopCategories: e.target.checked })}
                    />
                  }
                  label="Include Top Categories"
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailData.showCategoryNames}
                      onChange={(e) => setEmailData({ ...emailData, showCategoryNames: e.target.checked })}
                    />
                  }
                  label="Show Proper Category Names"
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Recipients
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Add Recipient Email"
                    value={recipientInput}
                    onChange={(e) => setRecipientInput(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button 
                            onClick={handleAddEmailRecipient}
                            disabled={!recipientInput || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientInput)}
                          >
                            Add
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddEmailRecipient();
                      }
                    }}
                  />
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {emailData.recipients.map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      onDelete={() => handleRemoveEmailRecipient(index)}
                      size="medium"
                    />
                  ))}
                </Box>
                
                {emailData.recipients.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No recipients added yet
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setIsEmailDialogOpen(false)}
              disabled={emailLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSendEmailReport}
              variant="contained"
              disabled={emailLoading || emailData.recipients.length === 0}
              startIcon={<EmailIcon />}
            >
              {emailLoading ? 'Sending...' : 'Send Report'}
            </Button>
          </DialogActions>
        </Dialog>
        
        <Snackbar 
          open={!!error || !!success} 
          autoHideDuration={6000} 
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
        >
          {(error || success) && (
            <Alert 
              severity={error ? "error" : "success"} 
              onClose={() => {
                setError(null);
                setSuccess(null);
              }}
            >
              {error || success}
            </Alert>
          )}
        </Snackbar>
      </Box>
    </Container>
  );
};

export default FinancialReportScheduler;